import {gql} from '@apollo/client';
import React from 'react';

import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SubtemplateHistoryPanelState} from 'FileStates';
import CloneModal from 'folder-items/CloneModal';
import getConfig from 'getConfig';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import SubtemplateActions from 'notebook/entry-templates/SubtemplateActions';
import SubtemplateDAO from 'notebook/entry-templates/SubtemplateDAO';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import VersionHistoryPanel from 'notebook/version-history/VersionHistoryPanel';
import {
  getVersionsWithPublishableVersionInfos,
  isOpenVersionLatestDraftVersion,
} from 'notebook/version-history/VersionHistoryPanelUtil';
import {Version} from 'notebook/version-history/VersionHistoryTypes';
import notifications from 'notifications';
import {SubtemplatePublishingVersionHistoryPanelBenchlingQuery} from 'template-collectibles/publishing-ui/graphql/SubtemplateVersionHistoryPanelQuery';
import ReactPanelBaseView, {getTabAndPanelIds, Subpanel} from 'toolbar/ReactPanelBaseView';
import legacyRunModal from 'ui-library/legacyRunModal';

import {TemplateCollectionGraphQL} from '../../../../../graphql-fragments/notebook/templates/__generated/TemplateCollectionGraphQL';
import {
  SubtemplateHistoryPanelQuery,
  SubtemplateHistoryPanelQueryVariables,
} from './__generated/SubtemplateHistoryPanelQuery';

const fileStates = require('FileStates').default.get();

interface QueryingSubtemplateHistoryPanelProps {
  id: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  permissions?: any;
}

interface SubtemplateHistoryPanelProps extends QueryingSubtemplateHistoryPanelProps {
  collection?: TemplateCollectionGraphQL;
}

interface EnhancedProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  versionState: any;
  collectionId?: string;
}

class SubtemplateHistoryPanelConnector extends DataConnector<SubtemplateHistoryPanelProps, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [SubtemplateStore];

  enhanceProps(): EnhancedProps {
    return {
      versionState: this.subtemplateStore().getVersionState(this.props.id),
      collectionId: this.subtemplateStore().getCollection().id,
    };
  }
}

export class SubtemplateHistoryPanel extends React.Component<SubtemplateHistoryPanelProps & EnhancedProps> {
  override render(): JSX.Element {
    const versions: Array<Version> = this.props.versionState.versions.toReversed();
    if (getConfig('ELN_ENABLE_TEMPLATE_PUBLISHING')) {
      return (
        <SubtemplatePublishingVersionHistoryPanelBenchlingQuery subtemplateId={this.props.id}>
          {(publishingRecords) => {
            publishingRecords.reverse();
            const versionsWithPublishing = getVersionsWithPublishableVersionInfos(
              versions,
              publishingRecords
            );
            const isLatestDraftVersion = isOpenVersionLatestDraftVersion(
              versionsWithPublishing,
              this.props.versionState.openVersionId
            );
            const disableResetToVersion: boolean = isLatestDraftVersion || this.props.versionState.unsaved;
            return (
              <VersionHistoryPanel
                versions={versionsWithPublishing}
                openVersionId={this.props.versionState.openVersionId}
                navigateAndSetOpenVersionId={this.openVersion}
                enableResetToVersion={!disableResetToVersion}
                resetToVersion={this.createDraftFromOpenVersion}
                enableDuplicateFromVersion={true}
                duplicateFromVersion={this.cloneFromVersion}
                versionParent={this.props.versionState.parent}
                navigateToVersionParent={this.navigateToVersionParent}
                unsaved={this.props.versionState.unsaved}
              />
            );
          }}
        </SubtemplatePublishingVersionHistoryPanelBenchlingQuery>
      );
    } else {
      const disableResetToVersion =
        this.props.versionState.isLatest ||
        this.props.versionState.unsaved ||
        !this.props.permissions.writable;
      return (
        <VersionHistoryPanel
          versions={versions}
          openVersionId={this.props.versionState.openVersionId}
          navigateAndSetOpenVersionId={this.openVersion}
          enableResetToVersion={!disableResetToVersion}
          resetToVersion={this.resetToVersion}
          enableDuplicateFromVersion={true}
          duplicateFromVersion={this.cloneFromVersion}
          versionParent={this.props.versionState.parent}
          navigateToVersionParent={this.navigateToVersionParent}
          unsaved={this.props.versionState.unsaved}
        />
      );
    }
  }

  openVersion = (versionId: number): void => {
    SubtemplateActions.loadVersion(this.props.id, versionId);
  };

  createDraftFromOpenVersion = async (): Promise<void> => {
    const result = await SubtemplateDAO.createDraftFromVersion(
      this.props.id,
      this.props.versionState.openVersionApiId
    );
    if (result) {
      const subtemplateVersionDbId = result.draftSubtemplateVersionDbId;
      SubtemplateActions.loadVersion(this.props.id, subtemplateVersionDbId);
      SubtemplateActions.fetchVersions(this.props.id);
    } else {
      notifications.error('Failed to create draft from version');
    }
  };

  resetToVersion = (): void => {
    SubtemplateActions.resetToOpenVersion(this.props.id);
  };

  cloneFromVersion = (): void => {
    legacyRunModal(CloneModal, {
      modelType: ModelTypeConstants.SUBTEMPLATE,
      id: this.props.id,
      versionId: this.props.versionState.openVersionId,
      initialName: `${this.props.name} (new)`,
      initialTemplateCollection: this.props.collection,
    });
  };

  navigateToVersionParent = (): void => {
    const parent = this.props.versionState.parent;

    fileStates.openFolderItem(parent.id, {
      open: true,
      source: 'history-panel-component',
      versionId: parent.versionId,
    });
  };
}

const SUBTEMPLATE_HISTORY_PANEL_QUERY = gql`
  query SubtemplateHistoryPanelQuery($id: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    templateCollection(id: $id) {
      ...TemplateCollectionGraphQL
    }
  }
  ${getFragmentWrapper('TemplateCollection').fragment}
`;

function QueryingSubtemplateHistoryPanel(
  props: QueryingSubtemplateHistoryPanelProps & EnhancedProps
): JSX.Element {
  const result = useSkippableBenchlingQuery<
    SubtemplateHistoryPanelQuery,
    SubtemplateHistoryPanelQueryVariables
  >(SUBTEMPLATE_HISTORY_PANEL_QUERY, {
    skip: props.collectionId == null,
    variables: {id: props.collectionId!},
  });

  const collection =
    result.type === QueryResultType.SUCCESS && !isInaccessible(result.data?.templateCollection)
      ? result.data.templateCollection
      : undefined;

  return (
    <SubtemplateHistoryPanel
      id={props.id}
      name={props.name}
      permissions={props.permissions}
      versionState={props.versionState}
      collection={collection}
    />
  );
}

const ConnectedSubtemplateHistoryPanel = SubtemplateHistoryPanelConnector.connect(
  QueryingSubtemplateHistoryPanel
);

export default class SubtemplateHistoryPanelView extends ReactPanelBaseView<
  SubtemplateHistoryPanelState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any
> {
  getSubpanels(): Array<Subpanel> {
    return [
      {
        ...getTabAndPanelIds('history'),
        title: 'History',
        hideInPanelTabs: true,
        component: (
          <ConnectedSubtemplateHistoryPanel
            id={this.model.id}
            name={this.model.name()}
            permissions={this.model.permissions()}
          />
        ),
      },
    ];
  }
}
